'use client';

import React, { useState } from 'react';

import { ArrowRight, Clock, BookOpen, Award, ChevronDown, SlidersHorizontal } from 'lucide-react';
import CourseCardModal from './CourseCardModal';

interface Programme {
  id: string;
  slug: string;
  title: string;
  label: string;
  category: string;
  categoryId: string;
  tier: 'qualification' | 'executive' | 'certificate' | 'cities';
  school: string;
  schoolId: string;
  duration: string;
  durationMonths: number;
  format: string;
  price: number;
  priceDisplay: string;
  monthlyPrice: string;
  description: string;
  modules: number;
  cpd: number;
  image: string;
  outcomes: string[];
  intakeDates: string[];
  tag?: string;
  tagColor?: string;
  comingSoon?: boolean;
  faculty?: string;
  topics?: string[];
}

const allProgrammes: Programme[] = [
// ── SCHOOL OF PROPERTY PRACTICE ──────────────────────────────────────────────
{
  id: 'q-001',
  slug: 'estate-agency-property-practice',
  title: 'Estate Agency & Property Practice',
  label: 'NQF Level 4 Qualification',
  category: 'Qualifications',
  categoryId: 'cat-qual',
  tier: 'qualification',
  school: 'School of Property Practice',
  schoolId: 'school-practice',
  duration: '6 months',
  durationMonths: 6,
  format: 'Self-Paced',
  price: 12500,
  priceDisplay: 'R 12,500',
  monthlyPrice: 'R 2,100/mo',
  description: 'The foundational qualification for aspiring estate agents — covering property legislation, ethics, conveyancing basics, FICA compliance, and professional practice standards.',
  modules: 10,
  cpd: 15,
  image: "/assets/images/course_estate_agency_practice.png",
  outcomes: ['Qualify for FFC registration', 'Understand property law fundamentals', 'Conduct compliant property transactions', 'Build a client acquisition system'],
  intakeDates: ['Rolling monthly intake'],
  tag: 'Entry Pathway',
  tagColor: 'bg-accent/10 text-accent border border-accent/20',
  faculty: 'School of Property Practice'
},
{
  id: 'q-002',
  slug: 'principal-property-practitioner',
  title: 'Principal Property Practitioner Programme',
  label: 'NQF Level 5 Qualification',
  category: 'Qualifications',
  categoryId: 'cat-qual',
  tier: 'qualification',
  school: 'School of Property Practice',
  schoolId: 'school-practice',
  duration: '10 months',
  durationMonths: 10,
  format: 'Online + Live Sessions',
  price: 22000,
  priceDisplay: 'R 22,000',
  monthlyPrice: 'R 2,200/mo',
  description: 'Advanced qualification for property professionals seeking principal status — covering business management, compliance, team leadership, and advanced property law.',
  modules: 14,
  cpd: 22,
  image: "/assets/images/course_principal_practitioner.png",
  outcomes: ['Qualify as a Principal Property Practitioner', 'Manage a property agency', 'Lead and develop agent teams', 'Ensure regulatory compliance'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  tag: 'Professional',
  tagColor: 'bg-primary/10 text-primary border border-primary/20',
  faculty: 'School of Property Practice'
},
{
  id: 'q-003',
  slug: 'real-estate-leadership',
  title: 'Real Estate Leadership',
  label: 'NQF Level 6 Pathway',
  category: 'Qualifications',
  categoryId: 'cat-qual',
  tier: 'qualification',
  school: 'School of Property Practice',
  schoolId: 'school-practice',
  duration: '12 months',
  durationMonths: 12,
  format: 'Online + Intensives',
  price: 32000,
  priceDisplay: 'R 32,000',
  monthlyPrice: 'R 2,800/mo',
  description: 'A future-facing leadership qualification for senior real estate professionals — combining strategic management, investment analysis, and institutional leadership.',
  modules: 18,
  cpd: 28,
  image: "/assets/images/course_real_estate_leadership.png",
  outcomes: ['Lead real estate organisations', 'Drive strategic property decisions', 'Manage institutional portfolios', 'Develop executive leadership capabilities'],
  intakeDates: ['Available 2026'],
  tag: 'Coming Soon',
  tagColor: 'bg-muted-foreground/10 text-muted-foreground border border-border',
  comingSoon: true,
  faculty: 'School of Property Practice'
},
{
  id: 'c-001',
  slug: 'facilities-property-management',
  title: 'Facilities & Property Management',
  label: 'Industry Certificate',
  category: 'Industry Certificates',
  categoryId: 'cat-cert',
  tier: 'certificate',
  school: 'School of Property Practice',
  schoolId: 'school-practice',
  duration: '6 months',
  durationMonths: 6,
  format: 'Self-Paced',
  price: 14500,
  priceDisplay: 'R 14,500',
  monthlyPrice: 'R 2,500/mo',
  description: 'Practical training in facilities management, building operations, lease administration, and property compliance for built environment professionals.',
  modules: 10,
  cpd: 14,
  image: "/assets/images/course_facilities_management.png",
  outcomes: ['Manage building facilities effectively', 'Administer lease agreements', 'Ensure property compliance', 'Implement preventive maintenance programmes'],
  intakeDates: ['Rolling monthly intake'],
  faculty: 'School of Property Practice'
},
{
  id: 'c-003',
  slug: 'property-marketing-client-psychology',
  title: 'Property Marketing & Client Psychology',
  label: 'Industry Certificate',
  category: 'Industry Certificates',
  categoryId: 'cat-cert',
  tier: 'certificate',
  school: 'School of Property Practice',
  schoolId: 'school-practice',
  duration: '4 months',
  durationMonths: 4,
  format: 'Online',
  price: 9500,
  priceDisplay: 'R 9,500',
  monthlyPrice: 'R 2,400/mo',
  description: 'Master the art of property marketing, digital strategy, and client psychology to build a high-performing sales and advisory practice.',
  modules: 8,
  cpd: 12,
  image: "/assets/images/course_property_marketing.png",
  outcomes: ['Build a property marketing strategy', 'Apply client psychology principles', 'Create compelling property listings', 'Generate qualified leads consistently'],
  intakeDates: ['Rolling monthly intake'],
  faculty: 'School of Property Practice'
},
// ── SCHOOL OF REAL ESTATE ─────────────────────────────────────────────────────
{
  id: 'e-001',
  slug: 'luxury-real-estate-advisory',
  title: 'Luxury Real Estate Advisory',
  label: 'Executive Certificate',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Real Estate',
  schoolId: 'school-real-estate',
  duration: '8 months',
  durationMonths: 8,
  format: 'Online + Live Sessions',
  price: 22000,
  priceDisplay: 'R 22,000',
  monthlyPrice: 'R 2,800/mo',
  description: 'Master the psychology, strategy, and craft of advising high-net-worth clients in premium residential and commercial property markets.',
  modules: 12,
  cpd: 18,
  image: "/assets/images/course_luxury_real_estate.png",
  outcomes: ['Close luxury residential sales', 'Build a HNW client portfolio', 'Master negotiation at the top end', 'Develop a personal luxury brand'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  faculty: 'School of Real Estate'
},
{
  id: 'e-003',
  slug: 'real-estate-investment-portfolio-strategy',
  title: 'Real Estate Investment & Portfolio Strategy',
  label: 'Executive Programme',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Real Estate',
  schoolId: 'school-real-estate',
  duration: '12 months',
  durationMonths: 12,
  format: 'Self-Paced',
  price: 28500,
  priceDisplay: 'R 28,500',
  monthlyPrice: 'R 2,700/mo',
  description: 'Build and manage high-performing property portfolios using institutional-grade investment analysis, deal structuring, and wealth-building strategies.',
  modules: 18,
  cpd: 24,
  image: "/assets/images/course_investment_portfolio.png",
  outcomes: ['Build a residential investment portfolio', 'Analyse commercial property returns', 'Structure REIT investments', 'Execute buy-to-let strategies'],
  intakeDates: ['14 July 2026', '6 October 2026', '12 January 2027'],
  faculty: 'School of Real Estate'
},
{
  id: 'e-005',
  slug: 'ai-proptech-real-estate',
  title: 'Artificial Intelligence & PropTech for Real Estate',
  label: 'Executive Technology Certificate',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Real Estate',
  schoolId: 'school-real-estate',
  duration: '6 months',
  durationMonths: 6,
  format: 'Online',
  price: 18500,
  priceDisplay: 'R 18,500',
  monthlyPrice: 'R 3,100/mo',
  description: 'Harness AI, machine learning, and PropTech tools to gain competitive advantage in valuations, market prediction, and data-driven investment decisions.',
  modules: 12,
  cpd: 18,
  image: "/assets/images/course_ai_proptech.png",
  outcomes: ['Build automated valuation models', 'Use AI for market analysis', 'Implement PropTech in your business', 'Create data-driven investment reports'],
  intakeDates: ['14 July 2026'],
  tag: 'New',
  tagColor: 'bg-accent/10 text-accent border border-accent/20',
  faculty: 'School of Real Estate'
},
{
  id: 'e-006',
  slug: 'commercial-real-estate-strategy',
  title: 'Commercial Real Estate Strategy',
  label: 'Executive Programme',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Real Estate',
  schoolId: 'school-real-estate',
  duration: '10 months',
  durationMonths: 10,
  format: 'Online + Live Sessions',
  price: 24000,
  priceDisplay: 'R 24,000',
  monthlyPrice: 'R 2,600/mo',
  description: 'Manage commercial, retail, and industrial properties at an institutional level — covering lease structuring, asset management, and portfolio optimisation.',
  modules: 16,
  cpd: 22,
  image: "/assets/images/course_facilities_management.png",
  outcomes: ['Manage commercial property portfolios', 'Structure and negotiate commercial leases', 'Implement asset management strategies', 'Oversee building operations and compliance'],
  intakeDates: ['6 October 2026', '12 January 2027'],
  faculty: 'School of Real Estate'
},
// ── SCHOOL OF PROPERTY DEVELOPMENT ───────────────────────────────────────────
{
  id: 'e-002',
  slug: 'strategic-property-development',
  title: 'Strategic Property Development',
  label: 'Advanced Professional Programme',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Property Development',
  schoolId: 'school-development',
  duration: '18 months',
  durationMonths: 18,
  format: 'Online + Intensives',
  price: 38500,
  priceDisplay: 'R 38,500',
  monthlyPrice: 'R 3,600/mo',
  description: 'A comprehensive programme covering the full property development lifecycle — from site acquisition and feasibility to construction management and portfolio exit.',
  modules: 22,
  cpd: 36,
  image: "/assets/images/course_property_development.png",
  outcomes: ['Complete development feasibility studies', 'Structure development finance', 'Manage construction contracts', 'Deliver residential and commercial projects'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  tag: 'Most Comprehensive',
  tagColor: 'bg-accent/10 text-accent border border-accent/20',
  faculty: 'School of Property Development'
},
{
  id: 'c-002',
  slug: 'construction-development-management',
  title: 'Construction & Development Management',
  label: 'Professional Certificate',
  category: 'Industry Certificates',
  categoryId: 'cat-cert',
  tier: 'certificate',
  school: 'School of Property Development',
  schoolId: 'school-development',
  duration: '16 months',
  durationMonths: 16,
  format: 'Online + Intensives',
  price: 36000,
  priceDisplay: 'R 36,000',
  monthlyPrice: 'R 2,500/mo',
  description: 'Covers the full construction and development management process — from project inception and procurement to site management, NHBRC compliance, and handover.',
  modules: 24,
  cpd: 32,
  image: "/assets/images/course_property_development.png",
  outcomes: ['Manage large-scale construction projects', 'Procure and manage contractors', 'Ensure NHBRC and SANS compliance', 'Control costs and timelines on complex developments'],
  intakeDates: ['6 October 2026'],
  faculty: 'School of Property Development'
},
{
  id: 'c-004',
  slug: 'development-feasibility-analysis',
  title: 'Development Feasibility Analysis',
  label: 'Industry Certificate',
  category: 'Industry Certificates',
  categoryId: 'cat-cert',
  tier: 'certificate',
  school: 'School of Property Development',
  schoolId: 'school-development',
  duration: '5 months',
  durationMonths: 5,
  format: 'Self-Paced',
  price: 11500,
  priceDisplay: 'R 11,500',
  monthlyPrice: 'R 2,300/mo',
  description: 'Learn to conduct rigorous development feasibility studies — from site analysis and cost modelling to financial projections and risk assessment.',
  modules: 9,
  cpd: 13,
  image: "/assets/images/course_investment_portfolio.png",
  outcomes: ['Conduct site feasibility studies', 'Build development cost models', 'Prepare financial projections', 'Present investment cases to stakeholders'],
  intakeDates: ['Rolling monthly intake'],
  faculty: 'School of Property Development'
},
// ── SCHOOL OF PROPERTY FINANCE ────────────────────────────────────────────────
{
  id: 'e-004',
  slug: 'real-estate-finance-capital-structuring',
  title: 'Real Estate Finance & Capital Structuring',
  label: 'Professional Certificate',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Property Finance',
  schoolId: 'school-finance',
  duration: '14 months',
  durationMonths: 14,
  format: 'Online',
  price: 32000,
  priceDisplay: 'R 32,000',
  monthlyPrice: 'R 2,500/mo',
  description: 'Deep-dive into property financing structures, development finance, REIT analysis, and capital markets — from South African banking practice to global property finance.',
  modules: 20,
  cpd: 28,
  image: "/assets/images/course_property_finance.png",
  outcomes: ['Originate and structure property loans', 'Analyse development finance proposals', 'Understand REIT capital structures', 'Model property-backed debt instruments'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  faculty: 'School of Property Finance'
},
{
  id: 'e-007',
  slug: 'urban-development-african-cities',
  title: 'Urban Development & African Cities',
  label: 'Advanced Programme',
  category: 'Executive Education',
  categoryId: 'cat-exec',
  tier: 'executive',
  school: 'School of Property Finance',
  schoolId: 'school-finance',
  duration: '12 months',
  durationMonths: 12,
  format: 'Online + Intensives',
  price: 21000,
  priceDisplay: 'R 21,000',
  monthlyPrice: 'R 1,900/mo',
  description: 'Explore the dynamics of urban growth, infrastructure investment, and real estate development across African cities and emerging markets.',
  modules: 15,
  cpd: 20,
  image: "/assets/images/course_african_cities.png",
  outcomes: ['Navigate urban development frameworks', 'Structure mixed-use developments', 'Engage communities effectively', 'Measure and report social impact'],
  intakeDates: ['14 July 2026', '12 January 2027'],
  faculty: 'School of Property Finance'
},
// ── SCHOOL OF CITIES & INFRASTRUCTURE ────────────────────────────────────────
{
  id: 'ci-001',
  slug: 'spatial-planning-land-use-management',
  title: 'Spatial Planning & Land Use Management',
  label: 'Professional Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '8 months',
  durationMonths: 8,
  format: 'Online + Live Sessions',
  price: 19500,
  priceDisplay: 'R 19,500',
  monthlyPrice: 'R 2,500/mo',
  description: 'Comprehensive grounding in SPLUMA, spatial development frameworks, land use rights, and municipal planning processes for built environment professionals.',
  modules: 12,
  cpd: 20,
  image: "/assets/images/course_urban_planning.png",
  outcomes: ['Apply SPLUMA in practice', 'Develop Spatial Development Frameworks', 'Navigate land use rights and applications', 'Engage with municipal planning processes'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Introduction to SPLUMA', 'Spatial Development Frameworks (SDFs)', 'Land Use Rights & Development Applications', 'Municipal Planning Processes']
},
{
  id: 'ci-002',
  slug: 'urban-planning-fundamentals',
  title: 'Urban Planning Fundamentals',
  label: 'Professional Certificate',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '6 months',
  durationMonths: 6,
  format: 'Online',
  price: 15500,
  priceDisplay: 'R 15,500',
  monthlyPrice: 'R 2,600/mo',
  description: 'Town planning essentials for property professionals — covering rezoning, consent use, township establishment, subdivision, and planning approvals.',
  modules: 10,
  cpd: 16,
  image: "/assets/images/course_urban_planning.png",
  outcomes: ['Navigate rezoning and consent use processes', 'Manage township establishment applications', 'Understand property rights and planning law', 'Ensure planning approvals and compliance'],
  intakeDates: ['Rolling monthly intake'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Town Planning for Property Professionals', 'Rezoning, Consent Use & Township Establishment', 'Subdivision & Consolidation', 'Property Rights & Planning Law', 'Planning Approvals & Compliance']
},
{
  id: 'ci-003',
  slug: 'housing-human-settlements',
  title: 'Housing & Human Settlements',
  label: 'Professional Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '9 months',
  durationMonths: 9,
  format: 'Online + Live Sessions',
  price: 21000,
  priceDisplay: 'R 21,000',
  monthlyPrice: 'R 2,400/mo',
  description: 'Practical knowledge in affordable housing development, student accommodation, human settlements planning, and housing policy delivery systems across South Africa.',
  modules: 14,
  cpd: 22,
  image: "/assets/images/course_african_cities.png",
  outcomes: ['Develop affordable housing projects', 'Plan student accommodation developments', 'Navigate human settlements frameworks', 'Implement housing policy and delivery systems'],
  intakeDates: ['14 July 2026', '12 January 2027'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Affordable Housing Development', 'Student Accommodation Development', 'Human Settlements Planning', 'Housing Policy & Delivery Systems']
},
{
  id: 'ci-004',
  slug: 'urban-development-regeneration',
  title: 'Urban Development & Regeneration',
  label: 'Advanced Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '12 months',
  durationMonths: 12,
  format: 'Online + Intensives',
  price: 26500,
  priceDisplay: 'R 26,500',
  monthlyPrice: 'R 2,300/mo',
  description: 'Advanced study in urban economics, mixed-use development, transit-oriented development, urban regeneration, and precinct planning for African cities.',
  modules: 16,
  cpd: 26,
  image: "/assets/images/course_african_cities.png",
  outcomes: ['Apply urban economics to development decisions', 'Structure mixed-use and TOD projects', 'Lead urban regeneration initiatives', 'Plan and develop precincts'],
  intakeDates: ['6 October 2026'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Urban Economics', 'Mixed-Use Development', 'Transit-Oriented Development', 'Urban Regeneration', 'Precinct Planning']
},
{
  id: 'ci-005',
  slug: 'infrastructure-planning-delivery',
  title: 'Infrastructure Planning & Delivery',
  label: 'Executive Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '10 months',
  durationMonths: 10,
  format: 'Online + Live Sessions',
  price: 24000,
  priceDisplay: 'R 24,000',
  monthlyPrice: 'R 2,500/mo',
  description: 'Strategic understanding of municipal infrastructure systems, infrastructure finance, public-private partnerships, and infrastructure-led development across Africa.',
  modules: 14,
  cpd: 22,
  image: "/assets/images/course_property_finance.png",
  outcomes: ['Plan and deliver municipal infrastructure', 'Structure infrastructure finance', 'Design and manage PPP frameworks', 'Lead infrastructure-led development projects'],
  intakeDates: ['14 July 2026', '6 October 2026'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Municipal Infrastructure Systems', 'Infrastructure Finance', 'Public-Private Partnerships', 'Infrastructure-Led Development']
},
{
  id: 'ci-006',
  slug: 'smart-cities-future-development',
  title: 'Smart Cities & Future Development',
  label: 'Executive Technology Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '6 months',
  durationMonths: 6,
  format: 'Online',
  price: 18500,
  priceDisplay: 'R 18,500',
  monthlyPrice: 'R 3,100/mo',
  description: 'Explore smart city frameworks, PropTech innovation, artificial intelligence in urban development, and data-driven planning for the cities of tomorrow.',
  modules: 10,
  cpd: 18,
  image: "/assets/images/course_ai_proptech.png",
  outcomes: ['Design smart city strategies', 'Apply PropTech and urban innovation', 'Use AI in urban development', 'Implement data-driven city planning'],
  intakeDates: ['Rolling monthly intake'],
  tag: 'New',
  tagColor: 'bg-accent/10 text-accent border border-accent/20',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Smart Cities', 'PropTech & Urban Innovation', 'Artificial Intelligence in Urban Development', 'Digital Cities & Data-Driven Planning']
},
{
  id: 'ci-007',
  slug: 'african-cities-urban-transformation',
  title: 'African Cities & Urban Transformation',
  label: 'Advanced Programme',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '8 months',
  durationMonths: 8,
  format: 'Online + Intensives',
  price: 20000,
  priceDisplay: 'R 20,000',
  monthlyPrice: 'R 2,600/mo',
  description: 'Comparative study of African urban development, future cities, and regional development strategies — positioning professionals to lead transformation across the continent.',
  modules: 12,
  cpd: 20,
  image: "/assets/images/course_african_cities.png",
  outcomes: ['Analyse comparative African urban development', 'Shape future city strategies', 'Develop regional development plans', 'Lead cross-border urban initiatives'],
  intakeDates: ['14 July 2026', '12 January 2027'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Comparative African Urban Development', 'Future Cities of Africa', 'Regional Development Strategies']
},
{
  id: 'ci-008',
  slug: 'property-intelligence-growth-corridors',
  title: 'Property Intelligence & Growth Corridors',
  label: 'Professional Certificate',
  category: 'Cities & Infrastructure',
  categoryId: 'cat-cities',
  tier: 'cities',
  school: 'School of Cities & Infrastructure',
  schoolId: 'school-cities',
  duration: '5 months',
  durationMonths: 5,
  format: 'Self-Paced',
  price: 14500,
  priceDisplay: 'R 14,500',
  monthlyPrice: 'R 3,000/mo',
  description: 'Learn to read municipal SDFs, analyse growth corridors, map development opportunities, and build real estate market intelligence for strategic investment decisions.',
  modules: 8,
  cpd: 14,
  image: "/assets/images/course_urban_planning.png",
  outcomes: ['Read and interpret municipal SDFs', 'Analyse growth corridors', 'Map development opportunities', 'Build real estate market intelligence'],
  intakeDates: ['Rolling monthly intake'],
  tag: 'New Faculty',
  tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  faculty: 'School of Cities & Infrastructure',
  topics: ['Reading Municipal SDFs', 'Growth Corridor Analysis', 'Development Opportunity Mapping', 'Real Estate Market Intelligence']
}];

// ── School metadata ───────────────────────────────────────────────────────────
const schoolConfig: Record<string, {
  name: string;
  description: string;
  separator: string;
  dot: string;
  labelBg: string;
  labelBorder: string;
  cardHover: string;
  accentLine: string;
}> = {
  'school-practice': {
    name: 'School of Property Practice',
    description: 'Foundational and advanced qualifications for estate agents, property practitioners, and built environment professionals — NQF-aligned and PPRA-compliant.',
    separator: 'border-accent',
    dot: 'bg-accent',
    labelBg: 'bg-accent/8 text-accent',
    labelBorder: 'border-accent/25',
    cardHover: 'hover:border-accent/30',
    accentLine: 'bg-accent'
  },
  'school-real-estate': {
    name: 'School of Real Estate',
    description: 'Executive education for real estate professionals in luxury advisory, commercial strategy, investment, and technology-driven property practice.',
    separator: 'border-primary',
    dot: 'bg-primary',
    labelBg: 'bg-primary/8 text-primary',
    labelBorder: 'border-primary/25',
    cardHover: 'hover:border-primary/30',
    accentLine: 'bg-primary'
  },
  'school-development': {
    name: 'School of Property Development',
    description: 'Comprehensive programmes for development professionals — from strategic development and construction management to feasibility analysis and the full project lifecycle.',
    separator: 'border-muted-foreground/40',
    dot: 'bg-muted-foreground/60',
    labelBg: 'bg-muted-foreground/8 text-muted-foreground',
    labelBorder: 'border-muted-foreground/20',
    cardHover: 'hover:border-muted-foreground/30',
    accentLine: 'bg-muted-foreground/50'
  },
  'school-finance': {
    name: 'School of Property Finance',
    description: 'Specialist finance education covering capital structuring, development finance, REIT analysis, and real estate investment strategy for finance professionals.',
    separator: 'border-blue-700',
    dot: 'bg-blue-700',
    labelBg: 'bg-blue-50 text-blue-800',
    labelBorder: 'border-blue-200',
    cardHover: 'hover:border-blue-300',
    accentLine: 'bg-blue-700'
  },
  'school-cities': {
    name: 'School of Cities & Infrastructure',
    description: 'Equipping leaders to shape the cities, communities, and infrastructure systems that drive economic growth and sustainable development across Africa.',
    separator: 'border-emerald-600',
    dot: 'bg-emerald-600',
    labelBg: 'bg-emerald-50 text-emerald-700',
    labelBorder: 'border-emerald-200',
    cardHover: 'hover:border-emerald-300',
    accentLine: 'bg-emerald-600'
  }
};

const schoolOrder = ['school-practice', 'school-real-estate', 'school-development', 'school-finance', 'school-cities'];

const sortOptions = [
{ id: 'sort-default', label: 'Default Order' },
{ id: 'sort-price-asc', label: 'Price: Low to High' },
{ id: 'sort-price-desc', label: 'Price: High to Low' },
{ id: 'sort-duration', label: 'Shortest First' }];


export default function ProgrammeGrid() {
  const [sortBy, setSortBy] = useState('sort-default');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSchool, setActiveSchool] = useState<string>('all');
  const [selectedProg, setSelectedProg] = useState<Programme | null>(null);

  const schoolFilters: Array<{id: string;label: string;}> = [
  { id: 'all', label: 'All Schools' },
  { id: 'school-practice', label: 'Property Practice' },
  { id: 'school-real-estate', label: 'Real Estate' },
  { id: 'school-development', label: 'Property Development' },
  { id: 'school-finance', label: 'Property Finance' },
  { id: 'school-cities', label: 'Cities & Infrastructure' }];


  const filtered = allProgrammes.filter((p) => {
    const matchesSchool = activeSchool === 'all' || p.schoolId === activeSchool;
    const matchesSearch = !searchQuery ||
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.school.toLowerCase().includes(searchQuery.toLowerCase()) || (
    p.faculty?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return matchesSchool && matchesSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'sort-price-asc':return a.price - b.price;
      case 'sort-price-desc':return b.price - a.price;
      case 'sort-duration':return a.durationMonths - b.durationMonths;
      default:return 0;
    }
  });

  // Group by school when showing all
  const groupedBySchool = activeSchool === 'all' ?
  schoolOrder.map((schoolId) => ({
    schoolId,
    programmes: sorted.filter((p) => p.schoolId === schoolId)
  })).filter((g) => g.programmes.length > 0) :
  null;

  const currentSortLabel = sortOptions.find((s) => s.id === sortBy)?.label ?? 'Sort';

  return (
    <div>
      {/* Course detail modal */}
      {selectedProg && (
        <CourseCardModal
          prog={selectedProg}
          config={schoolConfig[selectedProg.schoolId] ?? schoolConfig['school-practice']}
          onClose={() => setSelectedProg(null)}
        />
      )}

      {/* School filter tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {schoolFilters.map((filter) =>
        <button
          key={filter.id}
          onClick={() => setActiveSchool(filter.id)}
          className={`font-sans text-xs font-semibold px-4 py-2 rounded-xl border transition-all duration-200 ${
          activeSchool === filter.id ?
          filter.id === 'school-cities' ? 'bg-emerald-700 text-white border-emerald-700' :
          filter.id === 'school-finance' ? 'bg-blue-700 text-white border-blue-700' : 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'}`}>
          
            {filter.label}
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <span className="font-sans text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{sorted.length}</span> programmes
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search programmes..."
            className="pl-3 pr-3 py-2 bg-card border border-border rounded-xl text-sm font-sans w-52 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder-muted-foreground" />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-2 font-sans text-sm font-medium text-foreground bg-card border border-border px-3.5 py-2 rounded-xl hover:border-ring transition-colors">
            <SlidersHorizontal size={14} className="text-muted-foreground" />
            {currentSortLabel}
            <ChevronDown size={14} className="text-muted-foreground" />
          </button>
          {showSortDropdown &&
          <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-20 py-1 w-44">
              {sortOptions.map((opt) =>
            <button
              key={opt.id}
              onClick={() => {setSortBy(opt.id);setShowSortDropdown(false);}}
              className={`w-full text-left px-4 py-2.5 font-sans text-sm transition-colors ${sortBy === opt.id ? 'text-primary font-semibold bg-secondary' : 'text-foreground hover:bg-secondary'}`}>
                  {opt.label}
                </button>
            )}
            </div>
          }
        </div>
      </div>

      {/* Programme listing */}
      {sorted.length === 0 ?
      <div className="flex flex-col items-center justify-center py-20 text-center">
          <BookOpen size={40} className="text-muted-foreground/30 mb-4" />
          <h3 className="font-serif font-semibold text-xl text-foreground mb-2">No programmes found</h3>
          <p className="font-sans text-sm text-muted-foreground max-w-xs">
            No programmes match your current search. Try adjusting your filters or search terms.
          </p>
          <button
          onClick={() => {setSearchQuery('');setActiveSchool('all');}}
          className="mt-4 btn-outline px-6 py-2.5 rounded-xl text-sm">
            Clear Filters
          </button>
        </div> :
      groupedBySchool ?
      <div className="space-y-14">
            {groupedBySchool.map(({ schoolId, programmes: schoolProgs }) => {
          const config = schoolConfig[schoolId];
          return (
            <div key={schoolId}>
                  <div className="mb-8">
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`h-px flex-1 border-t ${config.separator} opacity-40`} />
                      <div className="flex items-center gap-3">
                        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                        <span className={`font-sans text-[0.6rem] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full border ${config.labelBg} ${config.labelBorder}`}>
                          Faculty
                        </span>
                        <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
                      </div>
                      <div className={`h-px flex-1 border-t ${config.separator} opacity-40`} />
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-foreground text-center mb-2">{config.name}</h3>
                    <p className="font-sans text-sm text-muted-foreground text-center max-w-xl mx-auto">
                      {config.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                    {schoolProgs.map((prog) =>
                <ProgrammeCard
                  key={prog.id}
                  prog={prog}
                  config={config}
                  onSelect={setSelectedProg} />
                )}
                  </div>
                </div>);

        })}
          </div> :
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {sorted.map((prog) =>
        <ProgrammeCard
          key={prog.id}
          prog={prog}
          config={schoolConfig[prog.schoolId] ?? schoolConfig['school-practice']}
          onSelect={setSelectedProg} />
        )}
          </div>
      }
    </div>);

}

interface ProgrammeCardProps {
  prog: Programme;
  config: typeof schoolConfig['school-practice'];
  onSelect: (prog: Programme) => void;
}

function ProgrammeCard({ prog, config, onSelect }: ProgrammeCardProps) {
  return (
    <div
      onClick={() => onSelect(prog)}
      className={`bg-card rounded-xl border border-border ${config.cardHover} transition-all duration-300 group overflow-hidden cursor-pointer ${prog.comingSoon ? 'opacity-70' : ''}`}>
      
      <div className="flex">
        <div
          className="w-32 flex-shrink-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{ backgroundImage: `url('${prog.image}')` }} />
        <div className="flex-1 p-5 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className={`font-sans text-[0.58rem] font-bold tracking-[0.1em] uppercase px-2 py-0.5 rounded-md border ${config.labelBg} ${config.labelBorder}`}>
              {prog.label}
            </span>
            {prog.tag &&
            <span className={`font-sans text-[0.58rem] font-bold px-2 py-0.5 rounded-md flex-shrink-0 ${prog.tagColor}`}>
                {prog.tag}
              </span>
            }
          </div>

          <h3 className="font-serif font-semibold text-base text-foreground mb-1 leading-snug group-hover:text-primary transition-colors duration-200">
            {prog.title}
          </h3>
          <p className="font-sans text-[0.68rem] text-accent mb-1.5 font-medium">{prog.school}</p>
          <p className="font-sans text-xs text-muted-foreground mb-3 line-clamp-2 leading-relaxed">
            {prog.description}
          </p>

          <div className="flex items-center gap-3 text-muted-foreground mb-3">
            <div className="flex items-center gap-1">
              <Clock size={11} />
              <span className="font-sans text-xs">{prog.duration}</span>
            </div>
            <div className="flex items-center gap-1">
              <BookOpen size={11} />
              <span className="font-sans text-xs">{prog.modules} modules</span>
            </div>
            <div className="flex items-center gap-1">
              <Award size={11} />
              <span className="font-sans text-xs">{prog.cpd} CPD pts</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="font-sans text-xs text-muted-foreground block">From</span>
              <span className="font-serif font-bold text-base text-primary">{prog.priceDisplay}</span>
            </div>
            {prog.comingSoon ?
            <span className="font-sans text-xs text-muted-foreground italic px-3 py-2">Available 2026</span> :
            <span className="flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-3.5 py-2 rounded-xl group-hover:bg-green-light transition-all duration-200">
                View Details
                <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            }
          </div>
        </div>
      </div>
    </div>);

}